import React, { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import { alpha, useTheme } from '@mui/material/styles'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import PersonIcon from '@mui/icons-material/Person'
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { savePlanningGroup } from '../detailApi'
import {
  isDuplicatePlanningGroupSaveError,
  extractDuplicatePlanningGroupValues,
  DUPLICATE_PLANNING_GROUP_MESSAGE_KEY
} from '../../../workflow2.0/planningGroupSaveError'
import { sunkenColor } from '../detailSurfaces'

// RN38/D18: planning group provenance icon — UNKNOWN gets a neutral icon (never invents an
// author), and an absent group (planningGroupOrigin === '') renders no icon at all.
const ORIGIN_ICONS = {
  MANUAL: PersonIcon,
  CONNECTOR: SettingsInputComponentIcon,
  INHERITED: ContentCopyIcon,
  UNKNOWN: HourglassEmptyIcon
}

// El campo del grupo comparte el aspecto de los campos editables del bloque General: el nivel
// hundido de la escala (`detailSurfaces.js`), no una superficie nueva.
const planningGroupFieldSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    background: sunkenColor(theme),
    fontWeight: 700,
    fontSize: '0.875rem',
    padding: '0 6px'
  },
  '& .MuiOutlinedInput-root .MuiAutocomplete-input': { padding: '2px 0' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.text.primary, 0.16) }
})

// The planning group of ONE row: the chip (or text), and its inline editor. The draft and the error
// live here, not in the table: a key press in the editor re-renders this cell and nothing else.
// Which row is being edited (only one at a time) is the table's, through `editing`.
const PlanningGroupCell = ({ row, fabricationOrderId, editing, canEdit, usedPlanningGroups, onStartEdit, onStopEdit, onSaved }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const [draft, setDraft] = useState('')
  const [error, setError] = useState('')
  // A second click can arrive before the first save answers.
  const saving = useRef(false)
  const OriginIcon = ORIGIN_ICONS[row.planningGroupOrigin]
  // Accessible name and tooltip of the provenance icon (RN38).
  const originTitle = (origin) => t_('Planning group origin: {{origin}}', {
    origin: { MANUAL: t_('Manual'), CONNECTOR: t_('CONNECTOR'), INHERITED: t_('Inherited'), UNKNOWN: t_('Unknown') }[origin] || t_('Unknown')
  })

  const startEdit = () => {
    // S461: solo el permiso decide; el estado de la operación ya no (RN40 y V-10 derogados).
    if (!canEdit) return
    setDraft(row.planningGroup ?? '')
    onStartEdit(row.jobReferenceId)
  }

  // N5: value and provenance are written in the same transaction on Sun's side — this row just
  // applies whatever the response says (RN38/RN39), it never re-derives MANUAL locally.
  // Sun answers MANUAL to every write, so a save with nothing changed would turn an INHERITED or
  // CONNECTOR group into a manual one (RN38): an unchanged value closes the editor with no request.
  const confirm = () => {
    if (saving.current) return
    const id = row.jobReferenceId
    const value = (draft ?? '').trim()
    // V-9: Jupiter replicates the checks of the legacy window, the 255 characters of the column.
    if (value.length > 255) {
      setError(t_('The planning group cannot be longer than 255 characters'))
      return
    }
    if (value === (row.planningGroup ?? '').trim()) {
      onStopEdit()
      setError('')
      return
    }
    saving.current = true
    savePlanningGroup(fabricationOrderId, id, value)
      .then((result) => {
        setError('')
        onSaved(id, result)
      })
      .catch((failure) => {
        // E7/S137: recognised by HTTP code AND the frozen literal, never reformulated — the row
        // keeps its previous group.
        if (isDuplicatePlanningGroupSaveError(failure)) {
          const values = extractDuplicatePlanningGroupValues(failure)
          setError(t_(DUPLICATE_PLANNING_GROUP_MESSAGE_KEY, { values: values.join(', ') }))
          return
        }
        setError(failure?.message || t_('Could not save the planning group'))
      })
      .finally(() => { saving.current = false })
  }

  return (
    <Box data-testid='operation-planning-group'>
      {editing
        ? (
          <Stack spacing={0.5}>
            <Stack direction='row' spacing={0.5} alignItems='center'>
              {/* Petición del PO (pasada 27): agrupar es poner VARIOS jobs en el
                      mismo grupo, y teclear la cadena a mano es justo como se cuelan
                      las erratas que rompen la agrupación en silencio (de ahí que
                      exista el error de grupo duplicado, E7/S137). Se ofrecen los
                      grupos YA USADOS en esta orden, derivados de las filas que N3 ya
                      trajo — ni catálogo, ni endpoint nuevo, ni nada de Sun.
                      Es un `Autocomplete freeSolo` de MUI (no un `datalist` nativo,
                      que se ve mal en Windows), NO una lista cerrada: escribir un
                      grupo nuevo es un caso de uso legítimo y tiene que seguir siendo
                      posible. El texto escrito es el borrador, igual que antes. */}
              <Autocomplete
                freeSolo
                size='small'
                options={usedPlanningGroups}
                inputValue={draft}
                onInputChange={(event, value) => setDraft(value)}
                sx={{ width: 170 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t_('Group name')}
                    sx={planningGroupFieldSx}
                    inputProps={{
                      ...params.inputProps,
                      'data-testid': `planning-group-input-${row.jobReferenceId}`,
                      'aria-label': t_('Planning group')
                    }}
                  />
                )}
              />
              <Button data-testid={`planning-group-confirm-${row.jobReferenceId}`} onClick={() => confirm()} size='small' sx={{ minWidth: 0, textTransform: 'none' }}>
                {t_('Save')}
              </Button>
              <Button data-testid={`planning-group-cancel-${row.jobReferenceId}`} onClick={onStopEdit} size='small' sx={{ minWidth: 0, textTransform: 'none' }}>
                {t_('Cancel')}
              </Button>
            </Stack>
          </Stack>
          )
      /* S371: la decisión de si se puede editar llega hasta el ASPECTO, no
                 solo hasta el comportamiento.
                 ⚠️ Esto tenía el `onClick` puesto SIEMPRE —la guarda vivía dentro de
                 `startEditGroup`, que salía sin hacer nada—, así que un `Chip` de
                 MUI se pintaba como pulsable (realce al pasar por encima, `role`
                 de botón, foco con el tabulador) aunque no hiciera absolutamente
                 nada. Un control falso invita a pulsarlo.
                 ⚠️ Es EL MISMO defecto que el PO marcó en la pasada 24 con el
                 nombre del workflow vestido de chip. Reapareció en este mismo
                 fichero porque aquella vez la lección se escribió en el sitio
                 arreglado y no en el patrón: **un elemento solo debe parecer
                 pulsable si al pulsarlo pasa algo**. Queda escrito aquí.
                 La guarda de `startEditGroup` se queda como red —defensa en
                 profundidad—, pero ya no es lo único que separa los dos estados. */
        : canEdit
          ? (
            <Chip
              data-testid={`planning-group-value-${row.jobReferenceId}`}
              onClick={() => startEdit()}
              size='small'
              icon={OriginIcon ? <OriginIcon data-testid={`planning-group-origin-icon-${row.planningGroupOrigin}`} titleAccess={originTitle(row.planningGroupOrigin)} sx={{ fontSize: '14px !important' }} /> : undefined}
              label={row.planningGroup || `+ ${t_('No group')}`}
              variant={row.planningGroup ? 'filled' : 'outlined'}
              sx={{
                cursor: 'pointer',
                fontWeight: 700,
                color: row.planningGroup ? 'text.primary' : 'text.disabled',
                backgroundColor: row.planningGroup ? alpha(theme.palette.text.primary, 0.06) : 'transparent'
              }}
            />
            )
          : (
            /* S371, segunda mitad: NO BASTA con quitarle el comportamiento.
                     Con solo eso, las píldoras se veían IDÉNTICAS en los dos estados
                     —comprobado en pantalla: siete "+ Sin grupo" indistinguibles— y
                     el control seguía invitando a pulsarlo aunque ya no respondiera.
                     Sin poder editar se presenta como TEXTO, que es como esta misma
                     ficha presenta todo lo de solo lectura: un control tiene forma
                     de control; lo que no se toca, no la tiene. Y desaparece el "+",
                     que era una invitación a añadir algo que no se puede añadir.
                     El icono de procedencia se queda: eso es información, no mando. */
            <Stack direction='row' spacing={0.5} alignItems='center'>
              {OriginIcon && <OriginIcon data-testid={`planning-group-origin-icon-${row.planningGroupOrigin}`} titleAccess={originTitle(row.planningGroupOrigin)} sx={{ fontSize: 14, color: 'text.disabled' }} />}
              <Typography
                data-testid={`planning-group-value-${row.jobReferenceId}`}
                component='span'
                variant='body2'
                sx={{ fontWeight: 700, color: row.planningGroup ? 'text.primary' : 'text.disabled' }}
              >
                {row.planningGroup || t_('No group')}
              </Typography>
            </Stack>
            )}
      {/* (Aquí vivió el motivo de «no editable», primero escrito bajo el grupo —repetía el
              estado, pasada 45— y después en su `title`. S461 lo quitó con la regla
              que lo producía: el grupo se edita en cualquier estado.) */}
      {error && (
        <Typography data-testid={`planning-group-error-${row.jobReferenceId}`} variant='caption' color='error' sx={{ display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  )
}

export default PlanningGroupCell
